// Contrat des quartiers et de la participation (M17, lot L5-C1, H7-04, D-224, D-250 à D-252) : quartiers
// du référentiel territorial existant, représentants (comptes usagers désignés pour une période dans un
// quartier), réunions et comptes rendus, besoins priorisés et orientés vers un signalement (M12) ou un
// projet de travaux (M14) par un acteur autorisé dans le domaine cible, consultations (question, options,
// période, public et règle de réponse explicites) et engagements de proximité (engagement_quartier, distinct
// de l'engagement budgétaire). Les réponses et résultats publics ont leurs schémas (participation-reponses.ts).
import { z } from 'zod';
import { schemaCodeCollectivite } from './usagers-comptes';

export * from './participation-reponses';

const uuid = z.string().uuid('Identifiant invalide');
const jour = z.iso.date('Date AAAA-MM-JJ attendue');
const instant = z.iso.datetime({ offset: true, message: 'Date et heure ISO 8601 attendue' });
const texte = (max: number, min = 1) => z.string().trim().min(min).max(max);
const facultatif = (max: number) => z.string().trim().max(max).optional();
const position = z.tuple([z.number().min(-180).max(180), z.number().min(-90).max(90)]);

export const TYPE_NUMERO_BESOIN = 'BES';
export const TYPE_NUMERO_CONSULTATION = 'CQT';

export const CATEGORIES_BESOIN = {
  voirie: 'Voirie et accès',
  eclairage: 'Éclairage public',
  assainissement: 'Assainissement et eau',
  proprete: 'Propreté',
  equipement: 'Équipement de quartier',
  securite: 'Sécurité',
  sante_education: 'Santé et éducation',
  autre: 'Autre',
} as const;
export type CategorieBesoin = keyof typeof CATEGORIES_BESOIN;
const CODES_CATEGORIES_BESOIN = Object.keys(CATEGORIES_BESOIN) as [CategorieBesoin, ...CategorieBesoin[]];

export const PRIORITES_BESOIN = { haute: 'Priorité haute', normale: 'Priorité normale', basse: 'Priorité basse' } as const;
export type PrioriteBesoin = keyof typeof PRIORITES_BESOIN;

export const ETATS_BESOIN_QUARTIER = {
  exprime: 'Exprimé',
  qualifie: 'Qualifié',
  oriente: 'Orienté',
  traite: 'Traité',
  rejete: 'Non retenu',
} as const;
export type EtatBesoinQuartier = keyof typeof ETATS_BESOIN_QUARTIER;

export const ORIGINES_BESOIN = { representant: 'Représentant du quartier', reunion: 'Réunion de quartier', agent: 'Service de proximité' } as const;
export type OrigineBesoin = keyof typeof ORIGINES_BESOIN;

/** Cibles d'orientation d'un besoin : objets des modules existants, créés ou liés avec les droits de leur domaine. */
export const CIBLES_ORIENTATION = { signalement: 'Signalement (interventions)', projet_travaux: 'Projet de travaux' } as const;
export type CibleOrientation = keyof typeof CIBLES_ORIENTATION;

export const ETATS_REUNION = { prevue: 'Prévue', tenue: 'Tenue', annulee: 'Annulée' } as const;
export type EtatReunion = keyof typeof ETATS_REUNION;

export const ETATS_ENGAGEMENT_QUARTIER = { pris: 'Pris', en_cours: 'En cours', tenu: 'Tenu', abandonne: 'Abandonné' } as const;
export type EtatEngagementQuartier = keyof typeof ETATS_ENGAGEMENT_QUARTIER;

/** Qui peut répondre à une consultation (D-251). */
export const PUBLICS_CONSULTATION = {
  usagers: 'Tout compte usager du portail',
  representants: 'Représentants du quartier en exercice',
} as const;
export type PublicConsultation = keyof typeof PUBLICS_CONSULTATION;

/** Répétition d'une réponse (D-251) : refusée ou remplaçant la précédente jusqu'à la clôture. */
export const REGLES_REPONSE = {
  unique: 'Une seule réponse par compte, définitive',
  modifiable: 'Une réponse par compte, modifiable jusqu\'à la clôture',
} as const;
export type RegleReponse = keyof typeof REGLES_REPONSE;

export const ETATS_CONSULTATION_QUARTIER = { brouillon: 'Brouillon', a_venir: 'Publiée, à venir', ouverte: 'Ouverte', close: 'Close' } as const;
export type EtatConsultationQuartier = keyof typeof ETATS_CONSULTATION_QUARTIER;

// --- Requêtes des agents ------------------------------------------------------------------------

export const schemaCreationQuartier = z.strictObject({ nom: texte(120, 2), arrondissement_id: uuid.optional() });
export type CreationQuartier = z.infer<typeof schemaCreationQuartier>;

/** Désignation d'un représentant : compte usager actif retrouvé par son identifiant exact (téléphone ou courriel). */
export const schemaDesignationRepresentant = z
  .strictObject({ identifiant: texte(200, 3), debut: jour, fin: jour, mandat: facultatif(300) })
  .refine((v) => v.fin >= v.debut, { message: 'La fin de la désignation suit son début', path: ['fin'] });
export type DesignationRepresentant = z.infer<typeof schemaDesignationRepresentant>;

export const schemaFinRepresentant = z.strictObject({ fin: jour, motif: texte(300, 3) });
export type FinRepresentant = z.infer<typeof schemaFinRepresentant>;

export const schemaCreationReunion = z.strictObject({ date: instant, lieu: texte(200, 2), objet: texte(300, 3) });
export type CreationReunion = z.infer<typeof schemaCreationReunion>;

export const schemaCompteRendu = z.strictObject({ compte_rendu: texte(8000, 10), participants: z.number().int().min(0).max(100_000) });
export type CompteRendu = z.infer<typeof schemaCompteRendu>;

export const schemaMotifParticipation = z.strictObject({ motif: texte(500, 3) });
export type MotifParticipation = z.infer<typeof schemaMotifParticipation>;

const champsBesoin = {
  titre: texte(200, 3),
  description: texte(4000, 3),
  categorie: z.enum(CODES_CATEGORIES_BESOIN),
  position: position.optional(),
  adresse: facultatif(300),
};

/** Besoin saisi par le service de proximité (origine agent ou réunion). */
export const schemaCreationBesoinQuartier = z.strictObject({
  ...champsBesoin,
  quartier_id: uuid,
  priorite: z.enum(['haute', 'normale', 'basse']).default('normale'),
  reunion_id: uuid.optional(),
});
export type CreationBesoinQuartier = z.input<typeof schemaCreationBesoinQuartier>;

export const schemaQualificationBesoin = z.strictObject({ priorite: z.enum(['haute', 'normale', 'basse']), observation: texte(2000, 3) });
export type QualificationBesoin = z.infer<typeof schemaQualificationBesoin>;

/**
 * Orientation : créer l'objet cible (avec les droits de création de son domaine) ou lier un objet existant
 * de la collectivité (avec le droit de le voir). Un rejeu ne crée jamais un second objet (D-250).
 */
export const schemaOrientationBesoin = z
  .strictObject({
    cible: z.enum(['signalement', 'projet_travaux']),
    objet_id: uuid.optional(),
    categorie_signalement: z.enum(['eclairage', 'voirie', 'depot_sauvage', 'assainissement', 'espaces_verts', 'mobilier_urbain', 'signalisation', 'batiment', 'autre']).optional(),
    debut_prevu: jour.optional(),
    fin_prevue: jour.optional(),
  })
  .refine((v) => v.objet_id || v.cible === 'signalement' || (v.debut_prevu && v.fin_prevue), { message: 'Un projet créé précise son début et sa fin prévus', path: ['debut_prevu'] })
  .refine((v) => !v.debut_prevu || !v.fin_prevue || v.debut_prevu <= v.fin_prevue, { message: 'La fin prévue suit le début', path: ['fin_prevue'] });
export type OrientationBesoin = z.infer<typeof schemaOrientationBesoin>;

export const schemaTraitementBesoin = z.strictObject({ observation: texte(2000, 3) });

export const schemaFiltreBesoinsQuartier = z.object({
  quartier_id: uuid.optional(),
  etat: z.enum(['exprime', 'qualifie', 'oriente', 'traite', 'rejete']).optional(),
  limite: z.coerce.number().int().min(1).max(500).default(200),
});

export const schemaCreationConsultationQuartier = z
  .strictObject({
    quartier_id: uuid,
    question: texte(500, 5),
    options: z.array(texte(120, 1)).min(2, 'Au moins deux options').max(6, 'Six options au plus'),
    debut: instant,
    fin: instant,
    public_cible: z.enum(['usagers', 'representants']),
    regle_reponse: z.enum(['unique', 'modifiable']),
  })
  .refine((v) => Date.parse(v.fin) > Date.parse(v.debut), { message: 'La fin de la consultation suit son début', path: ['fin'] })
  .refine((v) => new Set(v.options.map((o) => o.toLowerCase())).size === v.options.length, { message: 'Options en double', path: ['options'] });
export type CreationConsultationQuartier = z.infer<typeof schemaCreationConsultationQuartier>;

export const schemaCreationEngagementQuartier = z.strictObject({
  intitule: texte(300, 3),
  description: facultatif(4000),
  echeance: jour,
  besoin_id: uuid.optional(),
  reunion_id: uuid.optional(),
  service_id: uuid.optional(),
});
export type CreationEngagementQuartier = z.infer<typeof schemaCreationEngagementQuartier>;

export const schemaSuiviEngagementQuartier = z.strictObject({ etat: z.enum(['en_cours', 'tenu', 'abandonne']), observation: texte(2000, 3) });
export type SuiviEngagementQuartier = z.infer<typeof schemaSuiviEngagementQuartier>;

// --- Requêtes du portail (représentant, usager) ---------------------------------------------------

/** Besoin exprimé par un représentant, dans son quartier et pendant sa désignation. */
export const schemaExpressionBesoin = z.strictObject(champsBesoin);
export type ExpressionBesoin = z.infer<typeof schemaExpressionBesoin>;

/** Réponse d'un usager : rang de l'option choisie (0 pour la première). */
export const schemaReponseConsultation = z.strictObject({ collectivite: schemaCodeCollectivite, choix: z.number().int().min(0).max(5) });
export type ReponseConsultation = z.infer<typeof schemaReponseConsultation>;
